import { CalculateMonthCompletionParams, MonthlyData, OrderRecord } from '../types';

export type Tier = {
    consecutive_months: number;
    min_spend: number;
    max_spend?: number;
    promo_code: string;
};

export const findTierForAmount = (amount: number, tiers: Tier[] = []) => {
    for (const tier of tiers) {
        const min = tier.min_spend;
        const max = tier.max_spend ?? Infinity;
        if (amount >= min && amount < max) {
            return tier;
        }
    }
    return tiers[0];
};

export const extractVoucherFromPromoCode = (promoCode: string) => {
    const cleaned = promoCode?.split('-')?.[0]?.replace('NSR', '');
    return parseInt(cleaned) || 0;
};

const sortRecordsByDateDesc = (records: OrderRecord[] = []) =>
    [...records].sort((a, b) => (a.year === b.year ? b.month - a.month : b.year - a.year));

// Compare 1-indexed month from record (1-12) with JavaScript's 0-indexed getMonth() (0-11)
const isSameMonthYear = (record: OrderRecord, date: Date) =>
    record.month === date.getMonth() + 1 && record.year === date.getFullYear();

const getCurrentMonthRecords = (records: OrderRecord[], today: Date) =>
    records.filter((record) => isSameMonthYear(record, today));

const getCurrentMonthTotal = (records: OrderRecord[]) => records?.[0]?.totalAmount ?? 0;

const getApproxMonthTotal = (data: MonthlyData) =>
    parseInt(data?.approximateMonthlyTotalResponse?.currentMonthTotal ?? '0');

const getCurrentMonthEligibleAmount = (approxMonthTotal: number, tiers: Tier[], minAmount: number) => {
    if (approxMonthTotal < minAmount) return minAmount;
    const tier = findTierForAmount(approxMonthTotal, tiers);
    return tier?.min_spend ?? minAmount;
};

const isMonthEligible = (records: OrderRecord[], eligibleAmount: number) =>
    records.some((record) => record.totalAmount >= eligibleAmount);

const getWindowStartDate = (today: Date, maxMonths: number, includeCurrentMonth: boolean) =>
    includeCurrentMonth
        ? new Date(today.getFullYear(), today.getMonth() - (maxMonths - 1), 1)
        : new Date(today.getFullYear(), today.getMonth() - maxMonths, 1);

// Convert 1-indexed month (1-12) to 0-indexed (0-11) for JavaScript Date constructor
const filterRecentRecords = (records: OrderRecord[], windowStart: Date) =>
    records.filter((record) => new Date(record.year, record.month - 1) >= windowStart);

const filterPromoFreeRecords = (records: OrderRecord[]) =>
    records.filter((record) => record.promocode === null || record.promocode === undefined || record.promocode === '');

const dropCurrentMonthIfIneligible = (records: OrderRecord[], today: Date, isCurrentMonthEligible: boolean) =>
    isCurrentMonthEligible ? records : records.filter((record) => !isSameMonthYear(record, today));

const sliceRecentMonths = (records: OrderRecord[], maxMonths: number) => records.slice(0, maxMonths);

const areAllPromoCodesSameWhenInvalid = (promoCodes: Array<string | null | undefined> | undefined) => {
    if (!promoCodes) return false;
    const hasUndefined = promoCodes.some((code) => code === undefined);
    return hasUndefined ? promoCodes.every((code) => code === promoCodes[0]) : false;
};

// Returns 1-indexed month (1-12) to match the month format in OrderRecord data
const expectedMonthForIndex = (today: Date, isCurrentMonthEligible: boolean, index: number) => {
    const todayMonth = isCurrentMonthEligible ? today.getMonth() : today.getMonth() - 1;
    return new Date(today.getFullYear(), todayMonth - index).getMonth() + 1;
};

const hasPromoValue = (code: string | null | undefined) => code !== undefined && code !== '' && code !== null;

const isPromoBlank = (code: string | null | undefined) => code === undefined || code === '' || code === null;

type EvaluationContext = {
    minAmount: number;
    tiers: Tier[];
    isCurrentMonthEligible: boolean;
    today: Date;
    monthCompletionValue: number;
    voucherValue: number;
    promoCodes: Array<string | null | undefined> | undefined;
    allSamePromoCode: boolean;
};

const shouldStopForMonthMismatch = (record: OrderRecord, expectedMonth: number) =>
    record?.month && record.month !== expectedMonth;

const shouldInvalidateForSamePromo = (allSamePromoCode: boolean, promoCode: string | null | undefined) =>
    allSamePromoCode && hasPromoValue(promoCode);

const applyEligibleRecordUpdate = (
    recordTotal: number,
    context: Pick<EvaluationContext, 'tiers'>,
    monthCompletionValue: number,
    currentVoucherValue: number,
) => {
    const tier = findTierForAmount(recordTotal, context.tiers);
    const newVoucherValue = extractVoucherFromPromoCode(tier?.promo_code ?? '');
    // Use the minimum voucher across all consecutive qualifying months
    const voucherValue = monthCompletionValue === 0 ? newVoucherValue : Math.min(currentVoucherValue, newVoucherValue);

    return {
        monthCompletionValue: monthCompletionValue + 1,
        voucherValue,
    };
};

const evaluateRecentRecords = (records: OrderRecord[] | undefined, context: EvaluationContext) => {
    if (!records || !context.promoCodes) {
        return { monthCompletionValue: context.monthCompletionValue, voucherValue: context.voucherValue };
    }

    let voucherValue = context.voucherValue;
    let monthCompletionValue = context.monthCompletionValue;

    for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const promoCode = context.promoCodes[i];
        const expectedMonth = expectedMonthForIndex(context.today, context.isCurrentMonthEligible, i);

        if (shouldStopForMonthMismatch(record, expectedMonth)) {
            return { monthCompletionValue, voucherValue };
        }

        if (shouldInvalidateForSamePromo(context.allSamePromoCode, promoCode)) {
            return { monthCompletionValue: 0, voucherValue };
        }

        const meetsAmount = record.totalAmount >= context.minAmount;
        const noPromo = isPromoBlank(promoCode);

        if (meetsAmount && noPromo) {
            const update = applyEligibleRecordUpdate(record.totalAmount, context, monthCompletionValue, voucherValue);
            monthCompletionValue = update.monthCompletionValue;
            voucherValue = update.voucherValue;
            continue;
        }

        if (record.totalAmount < context.minAmount) {
            return { monthCompletionValue, voucherValue };
        }
    }

    return { monthCompletionValue, voucherValue };
};

export const calculateMonthCompletionValues = ({
    data,
    minAmount,
    tiers,
    initialVoucherValue,
    initialMonthCompletionValue,
    maxMonths,
    today,
}: CalculateMonthCompletionParams) => {
    let voucherValue = initialVoucherValue;
    let monthCompletionValue = initialMonthCompletionValue;

    if (!data) {
        return { monthCompletionValue, voucherValue };
    }

    const sortedRecords = sortRecordsByDateDesc(data.csmMonthlySubsOrderTotal as OrderRecord[]);
    const currentMonthRecords = getCurrentMonthRecords(sortedRecords, today);
    const hasCurrentMonthData = currentMonthRecords.length > 0;
    const currentMonthTotalAmount = getCurrentMonthTotal(currentMonthRecords);
    const approxMonthTotal = getApproxMonthTotal(data);
    const currentMonthEligibleAmount = getCurrentMonthEligibleAmount(approxMonthTotal, tiers as Tier[], minAmount);
    const isCurrentMonthEligible = isMonthEligible(currentMonthRecords, currentMonthEligibleAmount);

    const windowStart = getWindowStartDate(today, maxMonths, hasCurrentMonthData && isCurrentMonthEligible);
    const recentRecords = filterRecentRecords(sortedRecords, windowStart);
    const promoFreeRecords = filterPromoFreeRecords(recentRecords);
    const eligibleRecords = dropCurrentMonthIfIneligible(promoFreeRecords, today, isCurrentMonthEligible);
    const lastMonths = sliceRecentMonths(eligibleRecords, maxMonths);

    const promoCodes = lastMonths?.map((record) => record.promocode);
    const allSamePromoCode = areAllPromoCodesSameWhenInvalid(promoCodes);

    ({ monthCompletionValue, voucherValue } = evaluateRecentRecords(lastMonths, {
        minAmount,
        tiers: tiers as Tier[],
        isCurrentMonthEligible,
        today,
        monthCompletionValue,
        voucherValue,
        promoCodes,
        allSamePromoCode,
    }));

    // If current month's approximated total is below minimum, the streak is broken —
    // reset progress and show the minimum tier as the target voucher.
    if (approxMonthTotal < minAmount) {
        monthCompletionValue = 0;
        voucherValue = extractVoucherFromPromoCode((tiers[0] as Tier)?.promo_code ?? '');
        return { monthCompletionValue, voucherValue };
    }

    // If no eligible records were counted yet, fall back to minimum tier voucher as target
    if (monthCompletionValue === 0 && voucherValue === 0 && tiers.length > 0) {
        voucherValue = extractVoucherFromPromoCode((tiers[0] as Tier)?.promo_code ?? '');
    }

    // Current month is in-progress (actual < projected): recalculate voucher using projected tier
    // but respect the minimum voucher rule across all qualifying months
    if (!isCurrentMonthEligible && currentMonthTotalAmount >= minAmount) {
        const tier = findTierForAmount(approxMonthTotal, tiers as Tier[]);
        const projectedVoucher = extractVoucherFromPromoCode(tier?.promo_code ?? '');
        // Use the minimum between the calculated voucher and the projected voucher
        voucherValue = Math.min(voucherValue, projectedVoucher);
    }

    // Only count current month as +1 if its actual recorded amount meets the eligible threshold
    // (i.e. actual == approx, not a mid-month partial). Uses currentMonthEligibleAmount, not minAmount.
    if (
        !isCurrentMonthEligible &&
        monthCompletionValue < maxMonths &&
        currentMonthTotalAmount >= currentMonthEligibleAmount
    ) {
        monthCompletionValue += 1;
    }

    return { monthCompletionValue, voucherValue };
};
